import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  PackagePlus,
  RefreshCw,
  Search,
} from 'lucide-react';
import {
  createDraftFromWholesaler,
  fetchB2BNewProducts,
} from '../api.js';
import { Alert, Button, Loading, PageHeader, Panel, PanelBody } from '../ui/index.js';

function formatTnd(millimes: number | null): string {
  if (millimes === null || !Number.isFinite(millimes)) return '—';
  return (millimes / 1000).toFixed(3) + ' TND';
}

export function B2BNewProductsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [sourceKey, setSourceKey] = useState<string>('all');
  const [brand, setBrand] = useState('');
  const [search, setSearch] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ text: string; tone: 'info' | 'alarm' } | null>(null);

  const newProductsQuery = useQuery({
    queryKey: ['b2b-new-products', page, sourceKey, brand, search, inStockOnly],
    queryFn: () =>
      fetchB2BNewProducts({
        page,
        limit: 40,
        sourceKey: sourceKey === 'all' ? undefined : sourceKey,
        brand: brand || undefined,
        inStockOnly,
        q: search || undefined,
      }),
  });

  const createDraftMutation = useMutation({
    mutationFn: (offerId: number) => createDraftFromWholesaler(offerId),
    onSuccess: (data) => {
      setStatusMsg({
        text: `Produit brouillon créé avec succès : "${data.name}" (SKU: ${data.sku}). Prêt pour le catalogue WooCommerce.`,
        tone: 'info',
      });
      void queryClient.invalidateQueries({ queryKey: ['b2b-new-products'] });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => {
      setStatusMsg({ text: `Erreur création brouillon : ${String(err)}`, tone: 'alarm' });
    },
  });

  const data = newProductsQuery.data;
  const items = data?.items ?? [];
  const pagination = data?.pagination;
  const countsBySource = data?.countsBySource ?? {};

  const handleExportCsv = () => {
    if (items.length === 0) return;
    const headers = ['EAN', 'Reference', 'Designation', 'Marque', 'Grossiste', 'Prix_HT_TND', 'TVA', 'En_Stock'];
    const rows = items.map((i) => [
      `"${i.gtin}"`,
      `"${i.referenceRaw ?? ''}"`,
      `"${i.name.replace(/"/g, '""')}"`,
      `"${i.brand ?? ''}"`,
      `"${i.sourceLabel}"`,
      i.priceHt !== null ? (i.priceHt / 1000).toFixed(3) : '',
      i.vatRate ?? '',
      i.inStock === true ? 'OUI' : i.inStock === false ? 'NON' : 'INCONNU',
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `nouveautes-grossistes-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const portalTabs = [
    { key: 'all', label: 'Tous les grossistes' },
    { key: 'omnipharm', label: 'Omnipharm' },
    { key: 'santeaccess', label: 'Santé Access' },
    { key: 'medigros', label: 'Medigros' },
    { key: 'kilani', label: 'Kipropha' },
    { key: 'paraconnect', label: 'ParaConnect' },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        index="S20"
        title="Nouveautés Grossistes B2B"
        purpose="Produits détectés lors des crawls des 5 portails grossistes B2B, portant un code EAN-13 valide mais absents du catalogue MaPara. Exportez-les vers WooCommerce ou intégrez-les en 1 clic sous forme de brouillon prêt à la vente."
        meta={[
          { label: 'Nouveautés détectées', value: String(pagination?.total ?? '—') },
          { label: 'Grossiste sélectionné', value: portalTabs.find((t) => t.key === sourceKey)?.label ?? 'Tous' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={items.length === 0}>
              <Download className="h-3.5 w-3.5 mr-1" />
              Exporter CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void queryClient.invalidateQueries({ queryKey: ['b2b-new-products'] })}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Actualiser
            </Button>
          </div>
        }
      />

      {statusMsg && (
        <Alert tone={statusMsg.tone} title="Catalogue">
          {statusMsg.text}
        </Alert>
      )}

      {/* Wholesaler Tab Filters */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border pb-2">
        {portalTabs.map((tab) => {
          const count = tab.key === 'all'
            ? Object.values(countsBySource).reduce((a, b) => a + b, 0)
            : countsBySource[tab.key] ?? 0;
          const isActive = sourceKey === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setSourceKey(tab.key);
                setPage(1);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                isActive
                  ? 'bg-sky-600 text-white font-semibold shadow-xs'
                  : 'bg-card text-ink-soft hover:bg-cream hover:text-ink border border-border'
              }`}
            >
              <span>{tab.label}</span>
              {count > 0 && (
                <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-paper text-ink-ghost'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search & Filter Toolbar */}
      <Panel>
        <PanelBody className="py-3 px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[240px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-ink-ghost" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Rechercher désignation, EAN…"
                  className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-sky-600 focus:outline-none"
                />
              </div>

              <input
                type="text"
                value={brand}
                onChange={(e) => {
                  setBrand(e.target.value);
                  setPage(1);
                }}
                placeholder="Filtrer par marque…"
                className="w-36 rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-sky-600 focus:outline-none"
              />

              <label className="flex items-center gap-1.5 text-xs font-medium text-ink cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => {
                    setInStockOnly(e.target.checked);
                    setPage(1);
                  }}
                  className="rounded border-border text-sky-600 focus:ring-sky-600"
                />
                <span>En stock uniquement</span>
              </label>
            </div>

            <div className="text-xs text-ink-ghost">
              Page {pagination?.page ?? 1} / {pagination?.totalPages ?? 1} ({pagination?.total ?? 0} nouveautés)
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Main Table */}
      {newProductsQuery.isLoading ? (
        <Loading label="Recherche des nouveautés grossistes…" />
      ) : items.length === 0 ? (
        <Panel>
          <PanelBody className="py-12 text-center">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
            <p className="text-sm font-semibold text-ink">
              Aucun nouveau produit grossiste en attente pour ces critères.
            </p>
            <p className="text-xs text-ink-faint mt-1">
              Tous les produits crawlés portant un code EAN valide sont déjà répertoriés dans votre catalogue !
            </p>
          </PanelBody>
        </Panel>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-xs">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-border bg-paper/60 text-ink-ghost text-[11px] uppercase tracking-wider font-semibold">
                <th className="py-3 px-3.5">Code EAN & Ref Grossiste</th>
                <th className="py-3 px-3">Désignation Produit Découvert</th>
                <th className="py-3 px-3">Portail Grossiste</th>
                <th className="py-3 px-3 text-right">Prix Achat HT</th>
                <th className="py-3 px-3 text-center">TVA</th>
                <th className="py-3 px-3 text-center">Disponibilité</th>
                <th className="py-3 px-3 text-center">Action Catalogue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-cream/40 transition-colors">
                  {/* Barcode & Reference */}
                  <td className="py-3 px-3.5 whitespace-nowrap">
                    <div className="font-mono font-bold text-xs bg-sky-50 text-sky-800 px-2 py-0.5 rounded border border-sky-600/20 inline-block">
                      {item.gtin}
                    </div>
                    {item.referenceRaw && (
                      <div className="text-[10px] text-ink-ghost font-mono mt-0.5">
                        Ref: {item.referenceRaw}
                      </div>
                    )}
                  </td>

                  {/* Name & Brand */}
                  <td className="py-3 px-3 max-w-[320px]">
                    <p className="font-semibold text-ink leading-tight" title={item.name}>
                      {item.name}
                    </p>
                    {item.brand && (
                      <span className="text-[10px] font-semibold text-sky-700 bg-sky-50 px-1.5 py-0.2 rounded mt-1 inline-block">
                        {item.brand}
                      </span>
                    )}
                  </td>

                  {/* Wholesaler Source */}
                  <td className="py-3 px-3 whitespace-nowrap">
                    <span className="font-medium text-ink-soft bg-paper px-2 py-0.5 rounded border border-border">
                      {item.sourceLabel}
                    </span>
                    <div className="text-[10px] text-ink-ghost mt-0.5">
                      Détecté le {new Date(item.fetchedAt).toLocaleDateString('fr-FR')}
                    </div>
                  </td>

                  {/* Purchase Price HT */}
                  <td className="py-3 px-3 text-right whitespace-nowrap font-mono font-bold text-ink">
                    {formatTnd(item.priceHt)} <span className="text-[10px] text-ink-ghost font-normal">HT</span>
                    {item.priceTtc !== null && (
                      <div className="text-[10px] font-normal text-ink-ghost">
                        {formatTnd(item.priceTtc)} TTC
                      </div>
                    )}
                  </td>

                  {/* VAT */}
                  <td className="py-3 px-3 text-center whitespace-nowrap font-mono">
                    {item.vatRate !== null ? (
                      <span className="bg-paper text-ink px-1.5 py-0.5 rounded text-[11px] border border-border">
                        {item.vatRate}%
                      </span>
                    ) : (
                      <span className="text-ink-ghost text-[10px]">—</span>
                    )}
                  </td>

                  {/* Stock Status */}
                  <td className="py-3 px-3 text-center whitespace-nowrap">
                    <span
                      className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        item.inStock === true
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-600/20'
                          : item.inStock === false
                            ? 'bg-rose-50 text-rose-800 border border-rose-600/20'
                            : 'bg-paper text-ink-ghost border border-border'
                      }`}
                    >
                      {item.inStock === true ? 'En stock' : item.inStock === false ? 'Rupture' : 'Inconnu'}
                    </span>
                  </td>

                  {/* Add to Catalogue */}
                  <td className="py-3 px-3 text-center whitespace-nowrap">
                    <Button
                      variant="primary"
                      size="sm"
                      className="text-[11px] h-7 px-2.5 font-medium bg-sky-600 hover:bg-sky-700 text-white"
                      disabled={createDraftMutation.isPending}
                      onClick={() => createDraftMutation.mutate(item.id)}
                      title="Créer un brouillon dans le catalogue MaPara pour export Woo / Sage"
                    >
                      <PackagePlus className="h-3.5 w-3.5 mr-1" />
                      Créer Brouillon
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Footer */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <div className="text-xs text-ink-ghost">
            Affichage de {(page - 1) * pagination.limit + 1} à{' '}
            {Math.min(page * pagination.limit, pagination.total)} sur {pagination.total} nouveautés
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
